import React, { useEffect, useState } from "react";
import axios from "axios";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package, ShoppingCart, Users, Star, MessageCircle, Sparkles,
  Image as ImageIcon, Settings, ArrowRight, TrendingUp, AlertTriangle,
  ShoppingBag, Monitor, Flower2, CalendarDays, ArrowUpRight, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const STATUS_COLOR = {
  pending_payment: "#D4859B",
  paid: "#C5DB7B",
  accepted: "#8DBBA0",
  in_progress: "#7BA7C4",
  packed: "#B89BD4",
  out_for_delivery: "#E8A87C",
  delivered: "#A8C97B",
  completed: "#6DAD8A",
  cancelled: "#C4A098",
};

const STATUS_LABEL = {
  pending_payment: "Menunggu Bayar",
  paid: "Lunas",
  accepted: "Diterima",
  in_progress: "Diproses",
  packed: "Dikemas",
  out_for_delivery: "Dikirim",
  delivered: "Terkirim",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};

export default function AdminHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const topStats = [
    { label: "Hari Ini — Pesanan", value: data?.today_orders || 0, icon: CalendarDays, color: "#D4859B", sub: "online + POS" },
    { label: "Pendapatan Hari Ini", value: fmt(data?.today_revenue || 0), icon: TrendingUp, color: "#C5DB7B", sub: "online + POS" },
    { label: "Menunggu Konfirmasi", value: data?.pending_orders || 0, icon: Clock, color: "#E8A87C", sub: "pesanan online" },
    { label: "Stok Hampir Habis", value: (data?.low_stock_flowers || 0) + (data?.out_of_stock_flowers || 0), icon: AlertTriangle, color: "#E07070", sub: `${data?.out_of_stock_flowers || 0} habis` },
  ];

  const cmsCards = [
    { to: "/admin/landing", icon: Sparkles, label: "Landing Hero", desc: "Edit 4 hero sections, media, CTAs" },
    { to: "/admin/memory", icon: MessageCircle, label: "Memory AI", desc: "Tune AI system prompt" },
    { to: "/admin/about", icon: Star, label: "About Page", desc: "Hero, story sections, team" },
    { to: "/admin/settings", icon: Settings, label: "Site Settings", desc: "Brand, colors, audio, SEO" },
    { to: "/admin/media", icon: ImageIcon, label: "Media Library", desc: "Upload & manage all assets" },
    { to: "/admin/api-keys", icon: Settings, label: "API Keys", desc: "Kelola kunci integrasi" },
  ];

  return (
    <div data-testid="admin-overview">
      {/* Welcome */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-px bg-seikat-pink" />
          <span className="eyebrow-immersive">Dashboard</span>
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl text-[#1C1A19] leading-tight">
          Selamat datang di <em className="text-seikat-pink">Atelier</em>.
        </h1>
        <p className="font-body text-[#8A857D] mt-2 max-w-xl text-sm">
          Kelola seluruh operasional Seikat — penjualan, stok, konten, dan analitik dari satu tempat.
        </p>
      </div>

      {/* Today's highlight stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {topStats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5"
            data-testid={`today-stat-${i}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${s.color}22` }}>
                <s.icon size={15} style={{ color: s.color }} />
              </div>
            </div>
            <p className="font-heading text-2xl text-[#1C1A19] break-all">{loading ? "—" : s.value}</p>
            <p className="font-body text-[10px] tracking-widest uppercase text-[#A8A29A] mt-1">{s.label}</p>
            <p className="font-body text-[10px] text-[#C4B8AF] mt-0.5">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Overall stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {[
          { label: "Total Pesanan Online", value: data?.total_orders ?? 0, icon: ShoppingCart },
          { label: "Transaksi POS", value: data?.total_pos_orders ?? 0, icon: Monitor },
          { label: "Produk Aktif", value: data?.total_products ?? 0, icon: Package },
          { label: "Total User", value: data?.total_users ?? 0, icon: Users },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.05 }}
            className="bg-[#FDF8F6] rounded-2xl p-4 border border-[#D4859B]/10">
            <s.icon size={16} className="text-seikat-pink mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-[#1C1A19]">{loading ? "—" : s.value}</p>
            <p className="font-body text-[10px] tracking-widest uppercase text-[#A8A29A] mt-0.5">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-2 gap-5 mb-10">
        {/* Recent orders */}
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-[#1C1A19]">Pesanan Terbaru</h2>
            <NavLink to="/admin/orders-board" className="flex items-center gap-1 text-xs font-body text-seikat-pink hover:gap-2 transition-all">
              Lihat Semua <ArrowRight size={11} />
            </NavLink>
          </div>
          {loading ? (
            <p className="font-body text-sm text-[#A8A29A]">Memuat...</p>
          ) : data?.recent_orders?.length > 0 ? (
            <div className="space-y-2">
              {data.recent_orders.slice(0, 6).map(o => (
                <div key={o.order_id} className="flex items-center justify-between py-2 border-b border-[#D4859B]/8 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="font-body text-sm font-medium text-[#1C1A19] truncate">{o.shipping_name || o.customer_name || "Pelanggan"}</p>
                    <p className="font-body text-xs text-[#A8A29A] truncate">{o.order_id}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="font-body text-xs font-medium text-[#1C1A19]">{fmt(o.total_price)}</span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-body uppercase tracking-wide text-white"
                      style={{ background: STATUS_COLOR[o.status] || "#A8A29A" }}>
                      {STATUS_LABEL[o.status] || o.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingCart size={24} className="text-[#D4859B]/30 mx-auto mb-2" />
              <p className="font-body text-sm text-[#A8A29A]">Belum ada pesanan</p>
            </div>
          )}
        </div>

        {/* Quick links */}
        <div className="space-y-3">
          {[
            { to: "/admin/pos", icon: Monitor, label: "Buka POS Terminal", desc: "Transaksi penjualan langsung", color: "#D4859B" },
            { to: "/admin/inventory", icon: Flower2, label: "Manajemen Inventory", desc: `${data?.total_flowers || 0} jenis bunga • ${data?.low_stock_flowers || 0} hampir habis`, color: "#C5DB7B" },
            { to: "/admin/orders-board", icon: KanbanIcon, label: "Board Pesanan Online", desc: `${data?.in_progress || 0} sedang diproses • ${data?.pending_orders || 0} menunggu`, color: "#8DBBA0" },
            { to: "/admin/reviews", icon: Star, label: "Moderasi Review", desc: `${data?.pending_reviews || 0} review menunggu persetujuan`, color: "#B89BD4" },
          ].map((item, i) => (
            <motion.div key={item.to} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.06 }}>
              <NavLink to={item.to}
                className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-[#D4859B]/12 hover:border-seikat-pink/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}22` }}>
                  <item.icon size={18} style={{ color: item.color }} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium text-[#1C1A19]">{item.label}</p>
                  <p className="font-body text-xs text-[#A8A29A] truncate">{item.desc}</p>
                </div>
                <ArrowUpRight size={14} className="text-[#C4B8AF] group-hover:text-seikat-pink transition-colors flex-shrink-0" />
              </NavLink>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CMS modules */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-px bg-seikat-lime" />
          <span className="eyebrow-immersive">Modul CMS</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cmsCards.map((c, i) => (
          <motion.div key={c.to} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.04 }}>
            <NavLink to={c.to} data-testid={`cms-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group block bg-[#FDF8F6] rounded-2xl p-5 hover:bg-white hover:border-seikat-pink/20 hover:shadow-sm border border-transparent transition-all">
              <c.icon size={20} className="text-[#C4B8AF] group-hover:text-seikat-pink transition-colors mb-3" strokeWidth={1.4} />
              <h3 className="font-heading text-lg text-[#1C1A19] mb-1">{c.label}</h3>
              <p className="font-body text-xs text-[#A8A29A] mb-4">{c.desc}</p>
              <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-seikat-pink group-hover:gap-2 transition-all">
                Buka <ArrowRight size={11} />
              </span>
            </NavLink>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function KanbanIcon({ size = 18, style, strokeWidth = 1.5 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style?.color || "currentColor"} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}
